import type { Stripe } from '@stripe/stripe-js';
import { loadStripe } from '@stripe/stripe-js';

import { useUtilityContext } from '@change-corgi/core-react/utilityContext';

import { getStripeKey } from '#config/stripe';

export function useLoadStripe(): (stripe: Stripe | null, accountId?: string | null) => Promise<Stripe> {
	const utilityContext = useUtilityContext();

	return async function loadStripeWrapper(stripe: Stripe | null, accountId?: string | null): Promise<Stripe> {
		// if we already have a stripe instance and no accountId is specified, return it
		if (stripe && !accountId) return stripe;

		// otherwise load stripe with the specified accountId.
		// Stripe is a third-party API, so we resolve against the *api* environment
		// (which honours dev/staging proxy targets). For personalized demo hosts
		// (e.g. `jlou.changeeng.org` -> cyan) we prefer the resolved target so the
		// publishable key matches the back-end's configured Stripe account; we
		// fall back to the raw sub-environment so canonical demo hosts (jade,
		// cyan, ...) still resolve correctly.
		const environment = utilityContext.environment.getApiEnvironment();
		const subEnvironment =
			utilityContext.environment.getApiDemoEnvironmentTarget() ?? utilityContext.environment.getApiSubEnvironment();
		const stripeKey = getStripeKey({ environment, subEnvironment });
		const stripeInstance = accountId ? await loadStripe(stripeKey, { stripeAccount: accountId }) : stripe;
		if (!stripeInstance) throw new Error('Stripe instance not found');
		return stripeInstance;
	};
}
